import React, { useState, useEffect, useRef, memo } from 'react';
import { Trash2, AlertTriangle, AlertCircle } from 'lucide-react';
import { ProcessedMigrationItem, parseBrlNumber } from './csvSpreadsheetParser';

interface MigrationItemRowProps {
  item: ProcessedMigrationItem;
  onUpdateItem: (id: string, updates: Partial<ProcessedMigrationItem>) => void;
  onRemoveItem: (id: string) => void;
}

/**
 * Formata um número para string decimal com vírgula (ex: 12,50)
 */
function formatDecimalBR(val: number | undefined | null, decimals = 2): string {
  if (val === undefined || val === null || isNaN(val) || !isFinite(val)) return '0,00';
  return val.toFixed(decimals).replace('.', ',');
}

export const MigrationItemRow = memo(function MigrationItemRow({
  item,
  onUpdateItem,
  onRemoveItem
}: MigrationItemRowProps) {
  // Rastreia qual campo está focado no momento para evitar que recálculos sobrescrevam o que o usuário digita
  const focusedFieldRef = useRef<'unit' | 'cost' | 'margin' | 'sale' | 'stock' | 'minStock' | null>(null);

  // Últimos valores numéricos válidos garantidos (blindagem anti-NaN)
  const lastValidCostRef = useRef<number>(item.costPrice || 0);
  const lastValidMarginRef = useRef<number>(item.marginPercent !== undefined ? item.marginPercent : 40);
  const lastValidSaleRef = useRef<number>(item.salePrice || 0);
  const lastValidStockRef = useRef<number>(item.stockQuantity || 0);
  const lastValidMinStockRef = useRef<number>(item.minStock || 0);

  // Estados locais de string para permitir digitação livre (ex: "12," sem pulo de cursor)
  const [unitStr, setUnitStr] = useState<string>(item.unit || 'un');
  const [costStr, setCostStr] = useState<string>(() => formatDecimalBR(item.costPrice));
  const [marginStr, setMarginStr] = useState<string>(() => (item.marginPercent !== undefined ? item.marginPercent.toString() : '40'));
  const [saleStr, setSaleStr] = useState<string>(() => formatDecimalBR(item.salePrice));
  const [stockStr, setStockStr] = useState<string>(() => item.stockQuantity.toString());
  const [minStockStr, setMinStockStr] = useState<string>(() => (item.minStock !== undefined ? item.minStock.toString() : '0'));

  // Estados visuais de erro por campo
  const [costError, setCostError] = useState(false);
  const [marginError, setMarginError] = useState(false);
  const [saleError, setSaleError] = useState(false);

  // Sincroniza estados locais caso o item mude externamente e o campo NÃO esteja em foco
  useEffect(() => {
    if (focusedFieldRef.current !== 'cost') {
      setCostStr(formatDecimalBR(item.costPrice));
      lastValidCostRef.current = item.costPrice;
    }
    if (focusedFieldRef.current !== 'margin') {
      const m = item.marginPercent !== undefined ? item.marginPercent : 40;
      setMarginStr(m.toString());
      lastValidMarginRef.current = m;
    }
    if (focusedFieldRef.current !== 'sale') {
      setSaleStr(formatDecimalBR(item.salePrice));
      lastValidSaleRef.current = item.salePrice || 0;
    }
    if (focusedFieldRef.current !== 'unit') {
      setUnitStr(item.unit || 'un');
    }
    if (focusedFieldRef.current !== 'stock') {
      setStockStr(item.stockQuantity.toString());
      lastValidStockRef.current = item.stockQuantity;
    }
    if (focusedFieldRef.current !== 'minStock') {
      setMinStockStr((item.minStock !== undefined ? item.minStock : 0).toString());
      lastValidMinStockRef.current = item.minStock || 0;
    }
  }, [item.costPrice, item.marginPercent, item.salePrice, item.unit, item.stockQuantity, item.minStock]);

  // ──────────────────────────────────────────────────────────────────────────
  // 1. RECALCULO DE MARKUP DIVISOR (Custo -> Venda)
  // ──────────────────────────────────────────────────────────────────────────
  const handleCostChange = (raw: string) => {
    setCostStr(raw);
    if (!raw.trim()) {
      setCostError(false);
      return;
    }

    const parsed = parseBrlNumber(raw, -1);
    if (parsed >= 0) {
      setCostError(false);
      lastValidCostRef.current = parsed;

      // Markup Divisor: Venda = Custo / (1 - Margem/100)
      const margin = lastValidMarginRef.current;
      const marginDec = margin / 100;
      const divisor = marginDec < 1 ? Math.max(0.05, 1 - marginDec) : 0.6;
      const newSale = Math.round((parsed / divisor) * 100) / 100;
      lastValidSaleRef.current = newSale;

      if (focusedFieldRef.current !== 'sale') {
        setSaleStr(formatDecimalBR(newSale));
      }

      onUpdateItem(item.id, {
        costPrice: parsed,
        salePrice: newSale
      });
    } else {
      setCostError(true);
    }
  };

  const handleCostBlur = () => {
    focusedFieldRef.current = null;
    if (costError || !costStr.trim()) {
      setCostStr(formatDecimalBR(lastValidCostRef.current));
      setCostError(false);
    } else {
      setCostStr(formatDecimalBR(lastValidCostRef.current));
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 2. RECALCULO DE MARKUP DIVISOR (Margem -> Venda)
  // ──────────────────────────────────────────────────────────────────────────
  const handleMarginChange = (raw: string) => {
    setMarginStr(raw);
    if (!raw.trim()) {
      setMarginError(false);
      return;
    }

    const parsed = parseBrlNumber(raw, -999);
    // Margem por dentro deve ser menor que 100% e maior ou igual a -100%
    if (parsed >= -100 && parsed < 100) {
      setMarginError(false);
      lastValidMarginRef.current = parsed;

      const cost = lastValidCostRef.current;
      const marginDec = parsed / 100;
      const divisor = Math.max(0.05, 1 - marginDec);
      const newSale = Math.round((cost / divisor) * 100) / 100;
      lastValidSaleRef.current = newSale;

      if (focusedFieldRef.current !== 'sale') {
        setSaleStr(formatDecimalBR(newSale));
      }

      onUpdateItem(item.id, {
        marginPercent: parsed,
        salePrice: newSale
      });
    } else {
      setMarginError(true);
    }
  };

  const handleMarginBlur = () => {
    focusedFieldRef.current = null;
    if (marginError || !marginStr.trim()) {
      setMarginStr(lastValidMarginRef.current.toString());
      setMarginError(false);
    } else {
      setMarginStr(lastValidMarginRef.current.toString());
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 3. RECALCULO INVERSO DE MARKUP (Venda -> Margem)
  // ──────────────────────────────────────────────────────────────────────────
  const handleSaleChange = (raw: string) => {
    setSaleStr(raw);
    if (!raw.trim()) {
      setSaleError(false);
      return;
    }

    const parsed = parseBrlNumber(raw, -1);
    if (parsed >= 0) {
      setSaleError(false);
      lastValidSaleRef.current = parsed;

      const cost = lastValidCostRef.current;
      // Fórmula Inversa: Margem % = ((Venda - Custo) / Venda) * 100
      let newMargin = 0;
      if (parsed > 0) {
        newMargin = Math.round(((parsed - cost) / parsed) * 100);
      }
      lastValidMarginRef.current = newMargin;

      if (focusedFieldRef.current !== 'margin') {
        setMarginStr(newMargin.toString());
      }

      onUpdateItem(item.id, {
        salePrice: parsed,
        marginPercent: newMargin
      });
    } else {
      setSaleError(true);
    }
  };

  const handleSaleBlur = () => {
    focusedFieldRef.current = null;
    if (saleError || !saleStr.trim()) {
      setSaleStr(formatDecimalBR(lastValidSaleRef.current));
      setSaleError(false);
    } else {
      setSaleStr(formatDecimalBR(lastValidSaleRef.current));
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 4. UNIDADE, ESTOQUE FÍSICO E ESTOQUE MÍNIMO
  // ──────────────────────────────────────────────────────────────────────────
  const handleUnitChange = (raw: string) => {
    const clean = raw.trim().toLowerCase();
    setUnitStr(clean);
    onUpdateItem(item.id, { unit: clean || 'un' });
  };

  const handleStockChange = (raw: string) => {
    setStockStr(raw);
    const parsed = parseBrlNumber(raw, -1);
    if (parsed >= 0) {
      lastValidStockRef.current = parsed;
      onUpdateItem(item.id, { stockQuantity: parsed });
    }
  };

  const handleStockBlur = () => {
    focusedFieldRef.current = null;
    setStockStr(lastValidStockRef.current.toString());
  };

  const handleMinStockChange = (raw: string) => {
    setMinStockStr(raw);
    const parsed = parseBrlNumber(raw, -1);
    if (parsed >= 0) {
      lastValidMinStockRef.current = parsed;
      onUpdateItem(item.id, { minStock: parsed });
    }
  };

  const handleMinStockBlur = () => {
    focusedFieldRef.current = null;
    setMinStockStr(lastValidMinStockRef.current.toString());
  };

  // Identifica venda abaixo do custo (margem negativa / prejuízo)
  const isLoss = (item.salePrice !== undefined && item.costPrice > 0 && item.salePrice < item.costPrice);

  return (
    <tr className={`hover:bg-zinc-50/80 transition-colors ${isLoss ? 'bg-rose-50/30' : ''}`}>
      {/* 1. Código / SKU e Barcode */}
      <td className="p-2.5 font-mono text-[11px] text-zinc-600 align-middle">
        <div className="font-bold text-zinc-800">{item.sku || '—'}</div>
        {item.barcode && (
          <div className="text-[10px] text-zinc-400 font-mono tracking-tight" title="Código de Barras GTIN / EAN">
            EAN: {item.barcode}
          </div>
        )}
      </td>

      {/* 2. Nome do Material e Badges de Conciliação */}
      <td className="p-2.5 align-middle max-w-xs">
        <div className="font-semibold text-zinc-900 truncate text-xs" title={item.name}>
          {item.name}
        </div>
        
        {/* Badges da Cascata de Matching */}
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {item.matchType === 'sku' && (
            <span className="inline-flex items-center text-[10px] font-bold text-emerald-800 bg-emerald-100/80 border border-emerald-300 px-1.5 py-0.2 rounded">
              ✓ Match SKU
            </span>
          )}
          {item.matchType === 'barcode' && (
            <span className="inline-flex items-center text-[10px] font-bold text-emerald-800 bg-emerald-100/80 border border-emerald-300 px-1.5 py-0.2 rounded">
              ✓ Match EAN
            </span>
          )}
          {item.matchType === 'taxable_barcode' && (
            <span 
              className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-1.5 py-0.2 rounded"
              title="Atenção: Conciliado pelo EAN Tributário (cEANTrib). Verifique se este item é fardo/caixa ou unidade individual."
            >
              <AlertTriangle className="w-2.5 h-2.5 text-amber-700" />
              EAN Tributário (Confirmar)
            </span>
          )}
          {item.matchType === 'name' && (
            <span 
              className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-1.5 py-0.2 rounded"
              title="Atenção: Possível produto duplicado localizado apenas pelo nome. Verifique se o cadastro já existe antes de salvar."
            >
              <AlertCircle className="w-2.5 h-2.5 text-amber-700" />
              Nome similar
            </span>
          )}
        </div>
      </td>

      {/* 3. Unidade (Editável) */}
      <td className="p-2 text-center align-middle">
        <input
          type="text"
          value={unitStr}
          onFocus={() => { focusedFieldRef.current = 'unit'; }}
          onChange={(e) => handleUnitChange(e.target.value)}
          onBlur={() => { focusedFieldRef.current = null; }}
          placeholder="un"
          maxLength={6}
          title="Unidade de Medida (ex: un, kg, cx, l, m)"
          className="w-14 text-center uppercase font-mono text-xs font-bold bg-white border border-zinc-300 rounded-md px-1.5 py-1 text-zinc-800 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </td>

      {/* 4. Preço de Custo (Editável com máscara BR) */}
      <td className="p-2 text-right align-middle">
        <div className="relative inline-block w-24">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-zinc-400 pointer-events-none">
            R$
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={costStr}
            onFocus={() => { focusedFieldRef.current = 'cost'; }}
            onChange={(e) => handleCostChange(e.target.value)}
            onBlur={handleCostBlur}
            placeholder="0,00"
            title="Preço de Custo Unitário na NF"
            className={`w-full text-right font-medium text-xs bg-white rounded-md pl-6 pr-2 py-1 focus:outline-none focus:ring-1 ${
              costError 
                ? 'border-2 border-rose-500 bg-rose-50/50 text-rose-900 focus:ring-rose-500' 
                : 'border border-zinc-300 text-zinc-900 focus:border-brand focus:ring-brand'
            }`}
          />
        </div>
      </td>

      {/* 5. Margem (%) - Nova Coluna Interativa com Markup Divisor */}
      <td className="p-2 text-right align-middle">
        <div className="relative inline-flex items-center justify-end gap-1">
          <input
            type="text"
            inputMode="decimal"
            value={marginStr}
            onFocus={() => { focusedFieldRef.current = 'margin'; }}
            onChange={(e) => handleMarginChange(e.target.value)}
            onBlur={handleMarginBlur}
            placeholder="40"
            title="Margem de Lucro por Markup Divisor (%)"
            className={`w-14 text-right font-bold text-xs bg-white rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 ${
              isLoss 
                ? 'border-2 border-rose-500 bg-rose-50 text-rose-800 focus:ring-rose-500'
                : marginError
                  ? 'border-2 border-amber-500 bg-amber-50 text-amber-800 focus:ring-amber-500'
                  : 'border border-emerald-300 text-emerald-800 focus:border-brand focus:ring-brand'
            }`}
          />
          <span className={`text-[11px] font-bold ${isLoss ? 'text-rose-700' : 'text-emerald-700'}`}>%</span>
        </div>
        {isLoss && (
          <div className="mt-0.5">
            <span className="text-[9px] font-black text-rose-700 bg-rose-100 px-1 py-0.2 rounded border border-rose-300 uppercase tracking-tight inline-block">
              Prejuízo
            </span>
          </div>
        )}
      </td>

      {/* 6. Preço Venda Sugerido / Final (Editável) */}
      <td className="p-2 text-right align-middle">
        <div className="relative inline-block w-24">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-zinc-400 pointer-events-none">
            R$
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={saleStr}
            onFocus={() => { focusedFieldRef.current = 'sale'; }}
            onChange={(e) => handleSaleChange(e.target.value)}
            onBlur={handleSaleBlur}
            placeholder="0,00"
            title="Preço de Venda Final Sugerido"
            className={`w-full text-right font-bold text-xs bg-white rounded-md pl-6 pr-2 py-1 focus:outline-none focus:ring-1 ${
              isLoss
                ? 'border-2 border-rose-500 bg-rose-50 text-rose-900 focus:ring-rose-500'
                : saleError
                  ? 'border-2 border-rose-500 bg-rose-50/50 text-rose-900 focus:ring-rose-500'
                  : 'border border-emerald-300 text-brand focus:border-brand focus:ring-brand'
            }`}
          />
        </div>
      </td>

      {/* 7. Quantidade em Estoque (Editável) */}
      <td className="p-2 text-right align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={stockStr}
          onFocus={() => { focusedFieldRef.current = 'stock'; }}
          onChange={(e) => handleStockChange(e.target.value)}
          onBlur={handleStockBlur}
          placeholder="1"
          title="Quantidade física para entrada em estoque"
          className="w-18 text-right font-black text-xs bg-white border border-zinc-300 rounded-md px-2 py-1 text-zinc-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </td>

      {/* 8. Estoque Mínimo / Reposição - Nova Coluna */}
      <td className="p-2 text-right align-middle">
        <input
          type="text"
          inputMode="numeric"
          value={minStockStr}
          onFocus={() => { focusedFieldRef.current = 'minStock'; }}
          onChange={(e) => handleMinStockChange(e.target.value)}
          onBlur={handleMinStockBlur}
          placeholder="0"
          title="Estoque Mínimo de Segurança para disparar alerta de reposição"
          className="w-16 text-right font-bold text-xs bg-white border border-zinc-300 rounded-md px-2 py-1 text-zinc-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </td>

      {/* 9. Lote e Validade */}
      <td className="p-2.5 text-zinc-600 align-middle">
        {item.batchCode ? (
          <div className="inline-flex flex-col text-[10px] font-mono bg-zinc-100 px-1.5 py-0.5 rounded border border-zinc-200">
            <span className="font-bold text-zinc-800">{item.batchCode}</span>
            {item.expirationDate && (
              <span className="text-zinc-500">Val: {item.expirationDate}</span>
            )}
          </div>
        ) : (
          <span className="text-zinc-400 text-xs">—</span>
        )}
      </td>

      {/* 10. Ações */}
      <td className="p-2.5 text-center align-middle">
        <button
          type="button"
          onClick={() => onRemoveItem(item.id)}
          aria-label={`Excluir ${item.name}`}
          title="Excluir item da importação"
          className="inline-flex items-center justify-center rounded-md p-1.5 text-zinc-400 hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500 transition-colors cursor-pointer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
});
